import { useState } from "react";
import { signUp, signIn, resetPassword } from "../lib/db";
import { track } from "../lib/analytics";
import "../styles/welcome-surface.css";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/* The mark. It used to be two overlapping triangles — a summit, which is
   climb/conquer/peak iconography and exactly the achievement metaphor this
   product is not about. This is a still centre with two soft ripples around
   it: a steady presence, something that settles rather than something to be
   beaten. */
export function BrandGlyph({ size = 30 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <circle cx="12" cy="12" r="3" fill="currentColor" />
      <circle cx="12" cy="12" r="6.6" stroke="currentColor" strokeWidth="1.25" opacity="0.55" />
      <circle cx="12" cy="12" r="10.4" stroke="currentColor" strokeWidth="1.25" opacity="0.26" />
    </svg>
  );
}

/* The disclaimer is a promise about what this product is, so it stays on every
   entry screen — small, calm, and readable, never a wall of grey legalese. */
function Disclaimer({ full = false }) {
  return (
    <p className="ws-legal">
      <b>Elevate is in beta.</b> Support and reflection, not medical care.{" "}
      {full
        ? "Talk to a professional before starting something new — or any time you’re struggling."
        : "If you’re struggling, please talk to someone who can help."}
    </p>
  );
}

function WelcomeScreen({ onGetStarted, onSignIn }) {
  return (
    <main className="ws-screen" aria-label="Welcome to Elevate">
      <div className="ws-wash" aria-hidden="true" />

      <div className="ws-stage ws-enter">
        <div className="ws-mark">
          <span className="ws-mark-aura ds-breathe" aria-hidden="true" />
          <span className="ws-mark-glyph">
            <BrandGlyph size={44} />
          </span>
        </div>

        <p className="ws-wordmark">elevate</p>

        {/* The break is explicit. text-wrap:balance split this evenly — after
            the preposition, "Movement, for / how you feel." — and the comma is
            the only sensible break point in the sentence. */}
        <h1 className="ws-hero">
          Movement,
          <br />
          for how you feel.
        </h1>
        <p className="ws-hero-sub">
          Check in, move a little, then see what it did for your mood. Sized to
          the day you’re actually having.
        </p>
      </div>

      {/* Two buttons of the same size, told apart by fill. The returning-user
          action was a borderless ghost that read as another paragraph — for
          half the people who land here it is the most important control on
          the screen. */}
      <div className="ws-actions">
        <button type="button" className="btn btn-primary btn-lg btn-full" onClick={onGetStarted}>
          Get started
        </button>
        <button type="button" className="btn btn-secondary btn-lg btn-full" onClick={onSignIn}>
          I already have an account
        </button>
        <Disclaimer full />
      </div>
    </main>
  );
}

export default function AuthScreen({ onAuth }) {
  const [screen, setScreen] = useState("welcome");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [username, setUsername] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [confirmMsg, setConfirmMsg] = useState("");

  if (screen === "welcome") {
    return (
      <WelcomeScreen
        onGetStarted={() => setScreen("signup")}
        onSignIn={() => setScreen("signin")}
      />
    );
  }

  const mode = screen;

  const handleSignUp = async () => {
    setError("");
    setConfirmMsg("");
    setLoading(true);
    try {
      const data = await signUp(email.trim(), password, username.trim());
      if (!data.session) {
        setConfirmMsg("Almost there — confirm your email, then come back and sign in.");
        setScreen("signin");
      } else {
        track("sign_up");
        onAuth(data.session);
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSignIn = async () => {
    setError("");
    setConfirmMsg("");
    setLoading(true);
    try {
      const data = await signIn(email.trim(), password);
      track("sign_in");
      onAuth(data.session);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const handleForgot = async () => {
    setError("");
    setConfirmMsg("");
    setLoading(true);
    try {
      await resetPassword(email.trim());
      setConfirmMsg("Reset link sent. Check your email when you get a moment.");
      setScreen("signin");
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit =
    mode === "signup" ? handleSignUp :
    mode === "forgot" ? handleForgot :
    handleSignIn;

  const validEmail = EMAIL_RE.test(email.trim());

  const canSubmit =
    mode === "signup"
      ? validEmail && password.length >= 6 && username.trim().length >= 2
      : mode === "forgot"
        ? validEmail
        : validEmail && password.length >= 1;

  const heading =
    mode === "signup" ? "Let’s set you up." :
    mode === "forgot" ? "Let’s get you back in." :
    "Welcome back.";

  const subheading =
    mode === "signup" ? "Two minutes, and nothing here is permanent." :
    mode === "forgot" ? "Tell us your email and we’ll send a link to reset your password." :
    "Good to see you again.";

  const submitLabel =
    mode === "signup" ? "Create my account" :
    mode === "forgot" ? "Send me a link" :
    "Sign in";

  const loadingLabel =
    mode === "signup" ? "Setting things up…" :
    mode === "forgot" ? "Sending…" :
    "Signing you in…";

  /* Names what is still missing, in plain words, so a person facing a button
     they can't press knows why. Held back until they've started typing —
     listing three things before anyone has touched the form would be nagging. */
  const missing = [];
  if (mode === "signup" && username.trim().length < 2) missing.push("a name to call you");
  if (!validEmail) missing.push("your email");
  if (mode !== "forgot" && (mode === "signup" ? password.length < 6 : password.length < 1)) {
    missing.push(mode === "signup" ? "a password of 6+ characters" : "your password");
  }
  const started = Boolean(username || email || password);
  const needHint = !canSubmit && started ? `Still to add: ${missing.join(", ")}.` : "";

  const switchMode = (m) => {
    setScreen(m);
    setError("");
    setConfirmMsg("");
  };

  return (
    <main className="ws-screen" aria-label="Sign in to Elevate">
      <div className="ws-wash" aria-hidden="true" />

      <div className="ws-ob-head">
        <button
          type="button"
          className="ws-back"
          onClick={() => switchMode(mode === "forgot" ? "signin" : "welcome")}
          aria-label={mode === "forgot" ? "Back to sign in" : "Back to welcome"}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M15 18l-6-6 6-6" />
          </svg>
        </button>
        <span className="ws-head-mark" aria-hidden="true">
          <BrandGlyph size={20} />
        </span>
        <span className="ws-head-spacer" aria-hidden="true" />
      </div>

      <div className="ws-auth ws-enter" key={mode}>
        <div className="ws-auth-head">
          <h1 className="ws-auth-title">{heading}</h1>
          <p className="ws-auth-sub">{subheading}</p>
        </div>

        {/* Two plain buttons, not an ARIA tablist. This swaps the whole form
            rather than a panel, and the tabs pattern was only half-built —
            arrow keys did nothing. aria-pressed states what is true. */}
        {mode !== "forgot" && (
          <div className="ws-segment" role="group" aria-label="Sign in or create an account">
            {["signin", "signup"].map((m) => (
              <button
                key={m}
                type="button"
                aria-pressed={mode === m}
                className="ws-segment-btn"
                onClick={() => switchMode(m)}
              >
                {m === "signin" ? "Sign in" : "Create account"}
              </button>
            ))}
          </div>
        )}

        {/* A real <form>: iOS shows a "Go" return key, Enter submits from every
            field (it silently did nothing in the username field before), and
            iCloud Keychain / password managers can offer to fill and save. */}
        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (canSubmit && !loading) handleSubmit();
          }}
        >
          <div id="ws-auth-fields">
            {mode === "signup" && (
              <div className="ws-field">
                <label htmlFor="ws-username">What should we call you?</label>
                <input
                  id="ws-username"
                  placeholder="e.g. james"
                  value={username}
                  onChange={(e) =>
                    setUsername(e.target.value.replace(/[^a-zA-Z0-9_]/g, "").toLowerCase())
                  }
                  maxLength={20}
                  autoComplete="username"
                  aria-describedby="ws-username-hint"
                />
                <p className="ws-field-hint" id="ws-username-hint">
                  Letters, numbers and underscores.
                </p>
              </div>
            )}

            <div className="ws-field">
              <label htmlFor="ws-email">Email</label>
              <input
                id="ws-email"
                type="email"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="email"
              />
            </div>

            {mode !== "forgot" && (
              <div className="ws-field">
                <label htmlFor="ws-password">Password</label>
                <input
                  id="ws-password"
                  type="password"
                  placeholder={mode === "signup" ? "At least 6 characters" : "Your password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete={mode === "signup" ? "new-password" : "current-password"}
                />
              </div>
            )}
          </div>

          <div role="status" aria-live="polite">
            {error && <p className="ws-notice">{error}</p>}
            {confirmMsg && <p className="ws-notice ws-notice--good">{confirmMsg}</p>}
          </div>

          <div className="ws-auth-actions">
            <p className="ws-need" role="status" aria-live="polite">
              {needHint}
            </p>

            <button
              type="submit"
              className="btn btn-primary btn-lg btn-full"
              disabled={!canSubmit || loading}
            >
              {loading ? (
                <>
                  <span className="ws-spinner" aria-hidden="true" />
                  {loadingLabel}
                </>
              ) : (
                submitLabel
              )}
            </button>

            <div className="ws-auth-alt">
              {mode === "signin" && (
                <button type="button" className="ds-link" onClick={() => switchMode("forgot")}>
                  I’ve forgotten my password
                </button>
              )}
              {mode === "forgot" && (
                <button type="button" className="ds-link" onClick={() => switchMode("signin")}>
                  Back to sign in
                </button>
              )}
            </div>
          </div>
        </form>
      </div>

      <Disclaimer />
    </main>
  );
}
